import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { authApi, ApiError } from '../services/authApi';
import { authStorage } from '../services/authStorage';
import { Document, Company } from '../types/auth';
import { config } from '../config';
import { constructDownloadUrl } from '../utils/urlUtils';
import '../styles/Downloads.css';

interface DownloadFile {
  id: string;
  name: string;
  type: 'pdf' | 'doc' | 'docx' | 'xls' | 'xlsx' | 'ppt' | 'pptx' | 'txt' | 'image' | 'zip' | 'mp4' | 'video';
  size: string;
  uploadDate: string;
  category: string;
  companyId?: number | null;
  companyName?: string;
  previewUrl?: string;
  downloadUrl: string;
  uploadedBy: string;
}

interface DownloadsProps {
  isAdmin?: boolean;
  currentUser?: {
    username: string;
    role: 'user' | 'admin';
  };
  showTitle?: boolean;
  forcedCategory?: string;
}

const Downloads: React.FC<DownloadsProps> = ({ isAdmin = false, currentUser, showTitle = true, forcedCategory }) => {
  const { t } = useTranslation();

  // State management
  const [documents, setDocuments] = useState<Document[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>(forcedCategory || 'all');
  const [selectedCompany, setSelectedCompany] = useState<string>('all');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<DownloadFile | null>(null);
  const [viewMode, setViewMode] = useState<'card' | 'list'>('list');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'date' | 'category' | 'company'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Initialize component
  useEffect(() => {
    loadDocuments();
    if (isAdmin) {
      loadCompanies();
    }
  }, [isAdmin]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      setError('');
      const token = authStorage.getAccessToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      const userData = authStorage.getUserData();
      if (!userData) {
        setError('User data not found. Please log in again.');
        return;
      }

      let documentsData: Document[] = [];

      // Check if user is admin (userType: 1) or company user (userType: 2)
      if (userData.userType === 1 || userData.companyId === null) {
        // Admin user - get all documents
        documentsData = await authApi.getAllDocuments(token);
      } else {
        // Company user - get general documents and company-specific documents
        const [generalDocs, companyDocs] = await Promise.all([
          authApi.getGeneralDocuments(token),
          authApi.getCompanyDocuments(token, userData.companyId)
        ]);
        documentsData = [...generalDocs, ...companyDocs];
      }

      setDocuments(documentsData);
    } catch (error) {
      console.error('Failed to load documents:', error);
      if (error instanceof ApiError) {
        setError(error.message);
      } else {
        setError('Failed to load documents. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const token = authStorage.getAccessToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      const companiesData = await authApi.getAllCompanies(token);
      setCompanies(companiesData);
    } catch (error) {
      console.error('Failed to load companies:', error);
      if (error instanceof ApiError) {
        setError(error.message);
      } else {
        setError('Failed to load companies. Please try again.');
      }
    }
  };

  // Helper function to clean filename by removing timestamp prefix
  const cleanFileName = (fileName: string): string => {
    // Check if filename starts with numbers followed by a dash
    const timestampPattern = /^\d+-(.+)$/;
    const match = fileName.match(timestampPattern);

    // If pattern matches, return the part after the dash, otherwise return original filename
    return match ? match[1] : fileName;
  };

  // Convert Document to DownloadFile for UI compatibility
  const convertToDownloadFile = (document: Document): DownloadFile => {
    const rawFileName = document.documentUrl.split('/').pop() || 'unknown';
    const fileName = cleanFileName(rawFileName);
    const fileExtension = fileName.split('.').pop()?.toLowerCase() || 'txt';

    // Use the utility function to construct properly encoded download URL
    const downloadUrl = constructDownloadUrl(config.api.baseUrl, document.documentUrl);

    return {
      id: document.id.toString(),
      name: fileName,
      type: fileExtension as any,
      size: 'Unknown', // File size not provided by API
      uploadDate: new Date(document.updatedAt).toISOString().split('T')[0],
      category: document.category,
      companyId: document.companyId,
      companyName: document.company?.companyName || (document.companyId === null ? 'All Companies' : 'Unknown Company'),
      downloadUrl: downloadUrl,
      uploadedBy: 'Admin' // updatedBy ID not mapped to username in API response
    };
  };

  // Convert documents to DownloadFiles
  const files = documents.map(convertToDownloadFile);

  // Get unique categories
  const categories = ['all', ...Array.from(new Set(documents.map(doc => doc.category)))];

  // Get unique companies for admin view - sorted alphabetically
  const companyOptions = isAdmin
    ? ['all', ...companies.map(c => c.companyName).sort((a, b) => a.localeCompare(b))]
    : ['all'];

  // Filter and sort files based on selection
  const filteredFiles = files.filter(file => {
    // If a forcedCategory is set, always filter to it
    const effectiveCategory = forcedCategory || selectedCategory;
    const categoryMatch = effectiveCategory === 'all' || file.category === effectiveCategory;
    const searchMatch = !searchTerm || file.name.toLowerCase().includes(searchTerm.toLowerCase());

    if (!isAdmin) {
      return categoryMatch && searchMatch;
    }

    let companyMatch = false;
    if (selectedCompany === 'all') {
      companyMatch = true;
    } else {
      companyMatch = file.companyName === selectedCompany;
    }

    return categoryMatch && companyMatch && searchMatch;
  }).sort((a, b) => {
    const mul = sortDir === 'asc' ? 1 : -1;
    if (sortBy === 'date') return mul * (new Date(a.uploadDate).getTime() - new Date(b.uploadDate).getTime());
    if (sortBy === 'category') return mul * a.category.localeCompare(b.category);
    if (sortBy === 'company') return mul * (a.companyName ?? '').localeCompare(b.companyName ?? '');
    return mul * a.name.localeCompare(b.name);
  });

  // Pagination logic
  const totalItems = filteredFiles.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedFiles = filteredFiles.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, selectedCompany, searchTerm, sortBy, sortDir]);

  const getFileIcon = (type: string) => {
    const iconMap = {
      pdf: '📄',
      doc: '📝',
      docx: '📝',
      xls: '📊',
      xlsx: '📊',
      ppt: '📊',
      pptx: '📊',
      txt: '📄',
      image: '🖼️',
      zip: '📦',
      mp4: '🎥',
      video: '🎥'
    };
    return iconMap[type as keyof typeof iconMap] || '📄';
  };

  const getFileTypeColor = (type: string) => {
    const colorMap = {
      pdf: '#ff4444',
      doc: '#4285f4',
      docx: '#4285f4',
      xls: '#0f9d58',
      xlsx: '#0f9d58',
      ppt: '#ff6900',
      pptx: '#ff6900',
      txt: '#666666',
      image: '#9c27b0',
      zip: '#795548',
      mp4: '#e91e63',
      video: '#e91e63'
    };
    return colorMap[type as keyof typeof colorMap] || '#666666';
  };

  const handleDownload = (file: DownloadFile) => {
    // Create a temporary link element to trigger download with clean filename
    const link = document.createElement('a');
    link.href = file.downloadUrl;
    // Use the clean filename (already cleaned by cleanFileName function)
    link.download = file.name;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleUpload = async (file: File, category: string, companyId: number | null) => {
    try {
      setError('');
      const token = authStorage.getAccessToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      await authApi.uploadDocument(token, {
        file,
        category,
        companyId
      });

      // Reload documents after successful upload
      await loadDocuments();
      setShowUploadModal(false);
    } catch (error) {
      console.error('Failed to upload document:', error);
      if (error instanceof ApiError) {
        setError(error.message);
      } else {
        setError('Failed to upload document. Please try again.');
      }
    }
  };

  const handleDelete = async (file: DownloadFile) => {
    setFileToDelete(file);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!fileToDelete) return;

    try {
      setError('');
      const token = authStorage.getAccessToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        setShowDeleteModal(false);
        setFileToDelete(null);
        return;
      }

      await authApi.deleteDocument(token, parseInt(fileToDelete.id));

      // Remove document from local state
      setDocuments(prev => prev.filter(doc => doc.id.toString() !== fileToDelete.id));
      setShowDeleteModal(false);
      setFileToDelete(null);
    } catch (error) {
      console.error('Failed to delete document:', error);
      if (error instanceof ApiError) {
        setError(error.message);
      } else {
        setError('Failed to delete document. Please try again.');
      }
      setShowDeleteModal(false);
      setFileToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setFileToDelete(null);
  };

  if (loading) {
    return (
      <div className="downloads-page">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading documents...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="downloads-page">
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <h3>Error Loading Documents</h3>
          <p>{error}</p>
          <button onClick={loadDocuments} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="downloads-page">
      {showTitle && (
        <div className="downloads-header">
          <h1>{t('downloads.title')}</h1>
          <div className="downloads-controls">
            <input
              type="text"
              placeholder="Search by file name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {!forcedCategory && (
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="filter-select"
              >
                <option value="all">{t('downloads.allCategories')}</option>
                {categories.filter(cat => cat !== 'all').map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            )}

            {isAdmin && (
              <select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                className="filter-select"
              >
                <option value="all">{t('downloads.allCompanies')}</option>
                {companyOptions.filter(comp => comp !== 'all').map(company => (
                  <option key={company} value={company}>{company}</option>
                ))}
              </select>
            )}

            <div className="view-toggle">
              <button
                className={`view-toggle-btn ${viewMode === 'card' ? 'active' : ''}`}
                onClick={() => setViewMode('card')}
                title={t('downloads.cardView') || 'Card View'}
              >
                <span className="view-icon">⊞</span>
              </button>
              <button
                className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
                title={t('downloads.listView') || 'List View'}
              >
                <span className="view-icon">☰</span>
              </button>
            </div>

            {isAdmin && (
              <button
                className="upload-button"
                onClick={() => setShowUploadModal(true)}
              >
                <span className="upload-icon">📤</span>
                {t('downloads.uploadFile')}
              </button>
            )}
          </div>
        </div>
      )}

      {!showTitle && (
        <div className="downloads-controls">
          <input
            type="text"
            placeholder="Search by file name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {!forcedCategory && (
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="filter-select"
            >
              <option value="all">{t('downloads.allCategories')}</option>
              {categories.filter(cat => cat !== 'all').map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          )}

          {isAdmin && (
            <select
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              className="filter-select"
            >
              <option value="all">{t('downloads.allCompanies')}</option>
              {companyOptions.filter(comp => comp !== 'all').map(company => (
                <option key={company} value={company}>{company}</option>
              ))}
            </select>
          )}

          <div className="view-toggle">
            <button
              className={`view-toggle-btn ${viewMode === 'card' ? 'active' : ''}`}
              onClick={() => setViewMode('card')}
              title={t('downloads.cardView') || 'Card View'}
            >
              <span className="view-icon">⊞</span>
            </button>
            <button
              className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title={t('downloads.listView') || 'List View'}
            >
              <span className="view-icon">☰</span>
            </button>
          </div>

          {isAdmin && (
            <button
              className="upload-button"
              onClick={() => setShowUploadModal(true)}
            >
              <span className="upload-icon">📤</span>
              {t('downloads.uploadFile')}
            </button>
          )}
        </div>
      )}

      <div className={`files-container ${viewMode}-view`}>
        {viewMode === 'card' ? (
          <div className="files-grid">
            {paginatedFiles.map(file => (
              <div key={file.id} className="file-card">
                <div className="file-preview">
                  <div
                    className="file-type-icon"
                    style={{ backgroundColor: getFileTypeColor(file.type) }}
                  >
                    <span className="file-icon">{getFileIcon(file.type)}</span>
                    <span className="file-extension">{file.type.toUpperCase()}</span>
                  </div>
                </div>

                <div className="file-info">
                  <h3 className="file-name" title={file.name}>{file.name}</h3>
                  <div className="file-meta">
                    <span className="file-size">{file.size}</span>
                    <span className="file-date">{file.uploadDate}</span>
                  </div>
                  <div className="file-details">
                    <span className="file-category">{file.category}</span>
                    <span className="file-company">{file.companyName}</span>
                  </div>
                  <div className="file-uploader">
                    {t('downloads.uploadedBy')}: {file.uploadedBy}
                  </div>
                </div>

                <div className="file-actions">
                  <button
                    className="download-btn"
                    onClick={() => handleDownload(file)}
                    title={t('downloads.download') || 'Download'}
                  >
                    {t('downloads.download')}
                  </button>
                  {isAdmin && (
                    <button
                      className="delete-btn"
                      onClick={() => handleDelete(file)}
                      title={t('downloads.delete') || 'Delete'}
                    >
                      <span className="delete-icon">🗑️</span>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="files-list">
            <div className="list-header">
              {(['name', 'date', 'category', 'company'] as const).map(col => (
                <button
                  key={col}
                  className={`list-header-item ${col} sort-header ${sortBy === col ? 'active' : ''}`}
                  onClick={() => {
                    if (sortBy === col) {
                      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortBy(col);
                      setSortDir('asc');
                    }
                  }}
                >
                  {col.charAt(0).toUpperCase() + col.slice(1)}
                  <span className="sort-arrow">
                    {sortBy === col ? (sortDir === 'asc' ? '▲' : '▼') : '▲▼'}
                  </span>
                </button>
              ))}
              {isAdmin && <div className="list-header-item actions">Actions</div>}
            </div>
            {paginatedFiles.map(file => (
              <div
                key={file.id}
                className="file-list-item clickable"
                onClick={() => handleDownload(file)}
                title={`Click to download ${file.name}`}
              >
                <div className="list-item-cell name">
                  <div className="file-name-with-icon">
                    <div
                      className="file-type-icon-small"
                      style={{ backgroundColor: getFileTypeColor(file.type) }}
                    >
                      <span className="file-icon-small">{getFileIcon(file.type)}</span>
                    </div>
                    <span className="file-name" title={file.name}>{file.name}</span>
                  </div>
                </div>
                <div className="list-item-cell date">{file.uploadDate}</div>
                <div className="list-item-cell category">
                  <span className="category-tag">{file.category}</span>
                </div>
                <div className="list-item-cell company">
                  <span className="company-tag">{file.companyName}</span>
                </div>
                {isAdmin && (
                  <div className="list-item-cell actions">
                    <button
                      className="delete-btn-small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(file);
                      }}
                      title={t('downloads.delete') || 'Delete'}
                    >
                      🗑️
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <div className="pagination-info">
            Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} files
          </div>
          <div className="pagination-controls">
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              ← Previous
            </button>

            <div className="pagination-numbers">
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(page => {
                  return page === 1 ||
                    page === totalPages ||
                    Math.abs(page - currentPage) <= 2;
                })
                .map((page, index, array) => (
                  <React.Fragment key={page}>
                    {index > 0 && array[index - 1] < page - 1 && (
                      <span className="pagination-ellipsis">...</span>
                    )}
                    <button
                      className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                ))}
            </div>

            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {filteredFiles.length === 0 && (
        <div className="no-files">
          <div className="no-files-icon">📁</div>
          <h3>{t('downloads.noFiles')}</h3>
          <p>{t('downloads.noFilesDescription')}</p>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadModal
          onClose={() => setShowUploadModal(false)}
          onUpload={handleUpload}
          categories={categories.filter(cat => cat !== 'all')}
          companies={companies}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && fileToDelete && (
        <div className="modal-overlay" onClick={cancelDelete}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm Delete</h2>
              <button className="close-button" onClick={cancelDelete}>×</button>
            </div>

            <div className="modal-content">
              <div className="warning-icon">⚠️</div>
              <p className="delete-message">
                Are you sure you want to delete this document?
              </p>
              <div className="file-info-delete">
                <div className="file-name-delete" title={fileToDelete.name}>
                  <strong>File:</strong> {fileToDelete.name}
                </div>
                <div className="file-category-delete">
                  <strong>Category:</strong> {fileToDelete.category}
                </div>
                <div className="file-company-delete">
                  <strong>Company:</strong> {fileToDelete.companyName}
                </div>
              </div>
              <p className="delete-warning">
                This action cannot be undone.
              </p>
            </div>

            <div className="modal-actions">
              <button
                className="cancel-button"
                onClick={cancelDelete}
              >
                Cancel
              </button>
              <button
                className="confirm-delete-button"
                onClick={confirmDelete}
              >
                Delete Document
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Upload Modal Component
interface UploadModalProps {
  onClose: () => void;
  onUpload: (file: File, category: string, companyId: number | null) => Promise<void>;
  categories: string[];
  companies: Company[];
}

const UploadModal: React.FC<UploadModalProps> = ({ onClose, onUpload, categories, companies }) => {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('General');
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [newCategory, setNewCategory] = useState('');
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) return;

    try {
      setUploading(true);
      setError('');

      const categoryToUse = showNewCategory ? newCategory : selectedCategory;

      await onUpload(selectedFile, categoryToUse, selectedCompanyId);
    } catch (error) {
      console.error('Upload failed:', error);
      setError('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="upload-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('downloads.uploadNewFile')}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <form className="upload-form" onSubmit={handleSubmit}>
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-group">
            <label>{t('downloads.selectFile')}</label>
            <input
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.mp4,.avi,.mov,.wmv,.jpg,.jpeg,.png,.gif,.bmp,.svg"
              onChange={handleFileSelect}
              required
              className="file-input"
              disabled={uploading}
            />
            {selectedFile && (
              <div className="selected-file">
                <span className="file-name">{selectedFile.name}</span>
                <span className="file-size">({(selectedFile.size / 1024 / 1024).toFixed(1)} MB)</span>
              </div>
            )}
          </div>

          <div className="form-group">
            <label>{t('downloads.category')}</label>
            <div className="category-selector">
              <select
                value={showNewCategory ? 'new' : selectedCategory}
                onChange={(e) => {
                  if (e.target.value === 'new') {
                    setShowNewCategory(true);
                  } else {
                    setShowNewCategory(false);
                    setSelectedCategory(e.target.value);
                  }
                }}
                className="form-select"
                disabled={uploading}
              >
                <option value="General">General</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
                <option value="new">{t('downloads.addNewCategory')}</option>
              </select>
              {showNewCategory && (
                <input
                  type="text"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  placeholder={t('downloads.enterCategoryName') || ''}
                  className="form-input"
                  required
                  disabled={uploading}
                />
              )}
            </div>
          </div>

          <div className="form-group">
            <label>{t('downloads.targetCompany')}</label>
            <select
              value={selectedCompanyId || ''}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedCompanyId(value === '' ? null : parseInt(value));
              }}
              className="form-select"
              disabled={uploading}
            >
              <option value="">{t('downloads.allCompanies')}</option>
              {companies.map(company => (
                <option key={company.id} value={company.id}>{company.companyName}</option>
              ))}
            </select>
          </div>

          <div className="form-actions">
            <button type="button" className="cancel-button" onClick={onClose} disabled={uploading}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="submit-button" disabled={!selectedFile || uploading}>
              {uploading ? 'Uploading...' : t('downloads.upload')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Downloads;
